import { Config, Header } from '../../config.api'
import { attemptTokenRefresh } from '../../tokenRefresh'

// Backoff schedule for reconnect attempts; caps out rather than growing
// unbounded so a long-lived tab still retries every 15s instead of giving up.
const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 15000]

// After this many failed reconnect attempts, tell the caller to fall back to
// REST polling so the page still gets data if the WS endpoint stays down.
const FALLBACK_AFTER_ATTEMPTS = 3

// The backend (jetty_maps_ws_handler.go) sends a {"type":"ping"} message
// every 25s independent of real data updates, purely as a liveness signal —
// this is how the JS client can tell "server is hung" apart from "server has
// nothing new to say" (WS protocol-level ping/pong isn't visible to JS at
// all). If nothing — ping or real update — arrives within this window, the
// connection is presumed dead and force-closed so the reconnect logic below
// kicks in; a plain network drop or frozen tab can otherwise leave
// ws.readyState stuck at OPEN indefinitely with no onclose ever firing.
const HEARTBEAT_TIMEOUT_MS = 75000
const HEARTBEAT_CHECK_INTERVAL_MS = 10000

const toWsUrl = (httpUrl) => httpUrl.replace(/^https/, 'wss').replace(/^http/, 'ws')

// Opens a live connection to /jetty_maps/ws and keeps it alive, re-fetching a
// (possibly refreshed) auth token on every (re)connect via the same Header()
// used by REST calls. Browsers can't attach custom headers to a WebSocket
// handshake, so the token travels as a query param instead (mirrored on the
// backend in AuthMiddleware).
//
// onUpdate({ type, location, data }) is called for every message pushed by
// the hub. onFallback() is called once, after a few failed reconnects, so the
// caller can fall back to its old REST polling instead of leaving the user
// with stale data indefinitely.
export function connectJettyMapsSocket({ onUpdate, onFallback }) {
  let ws = null
  let closedByClient = false
  let authFailed = false
  let reconnectAttempt = 0
  let reconnectTimer = null
  let pendingSubscribe = null
  let lastMessageAt = 0
  let watchdogTimer = null

  const handleTokenExpired = () => {
    authFailed = true
    if (reconnectTimer) clearTimeout(reconnectTimer)
    stopWatchdog()
    if (ws) ws.close()
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('token-expired', handleTokenExpired)
  }

  function stopWatchdog() {
    if (watchdogTimer) clearInterval(watchdogTimer)
    watchdogTimer = null
  }

  function startWatchdog() {
    stopWatchdog()
    lastMessageAt = Date.now()
    watchdogTimer = setInterval(() => {
      if (Date.now() - lastMessageAt > HEARTBEAT_TIMEOUT_MS) {
        // No ping or update in too long — the connection is presumed dead.
        // Closing it triggers onclose below, which runs the normal
        // reconnect path exactly as if the server had closed it.
        if (ws) ws.close()
      }
    }, HEARTBEAT_CHECK_INTERVAL_MS)
  }

  const scheduleReconnect = () => {
    if (closedByClient || authFailed) return
    stopWatchdog()
    reconnectAttempt += 1
    if (reconnectAttempt === FALLBACK_AFTER_ATTEMPTS && onFallback) {
      onFallback()
    }
    const delay = RECONNECT_DELAYS_MS[Math.min(reconnectAttempt - 1, RECONNECT_DELAYS_MS.length - 1)]
    // A reconnect means the previous attempt didn't just idle out cleanly —
    // force a real token refresh rather than trusting Header()'s passive
    // "still >30min from expiry" check, the same way apiClient.js forces a
    // refresh-and-retry on a 401 instead of trusting its own proactive
    // check: the backend can reject a token the client still believes is
    // fine (clock skew, revocation, etc.), and retrying with the same token
    // would just fail the same way again.
    reconnectTimer = setTimeout(() => open({ forceTokenRefresh: true }), delay)
  }

  const open = async ({ forceTokenRefresh = false } = {}) => {
    if (closedByClient) return

    if (forceTokenRefresh) {
      try {
        await attemptTokenRefresh()
      } catch {
        // attemptTokenRefresh() already fired 'token-expired' internally,
        // which handleTokenExpired() above reacts to — nothing more to do.
        authFailed = true
        return
      }
    }

    // Header() proactively refreshes the token when it's close to expiry
    // (see config.api.js _token()); if refresh has already failed elsewhere
    // it returns no token at all, which is our signal that reconnecting is
    // pointless — the shared 'token-expired' handling will already be
    // showing the session-expired modal.
    const { headers } = await Header()
    const token = headers['token-auth']
    if (!token) {
      authFailed = true
      return
    }

    const url = `${toWsUrl(Config.MAIN_SERVICE_ENDPOINT)}/jetty_maps/ws?token-auth=${encodeURIComponent(token)}`
    ws = new WebSocket(url)

    ws.onopen = () => {
      reconnectAttempt = 0
      startWatchdog()
      if (pendingSubscribe) {
        ws.send(JSON.stringify(pendingSubscribe))
      }
    }

    ws.onmessage = (event) => {
      lastMessageAt = Date.now()
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'ping') return
        onUpdate(msg)
      } catch {
        // ignore malformed message
      }
    }

    ws.onclose = () => {
      stopWatchdog()
      if (closedByClient) return
      scheduleReconnect()
    }
  }

  open()

  return {
    // Sends (or re-sends, on the next successful connect) a subscribe
    // message for the given location/filter/search — call this whenever the
    // Maps Jetty page's location or filter changes.
    subscribe(payload) {
      pendingSubscribe = { type: 'subscribe', ...payload }
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(pendingSubscribe))
      }
    },
    close() {
      closedByClient = true
      if (typeof window !== 'undefined') {
        window.removeEventListener('token-expired', handleTokenExpired)
      }
      if (reconnectTimer) clearTimeout(reconnectTimer)
      stopWatchdog()
      if (ws) ws.close()
    },
  }
}
